/**
 * Self-registration helpers — Competition.settings.registration + Entry ownership.
 * Does not change Entry / Competition aggregates (ADR-028).
 */

import { COMPETITION_STATUSES, COMPETITION_VISIBILITY } from "./competitionTypes";
import { normalizeCompetitionStatus } from "./competitionStatusTransitions";
import { ENTRY_STATUSES } from "./entryManagementTypes";
import { arenaMatchesSport } from "../sport/sportDiscoverScope";

/**
 * @param {object | null | undefined} competition
 */
export function getRegistrationSettings(competition) {
  const reg = competition?.settings?.registration || {};
  return {
    allowSelfRegister: reg.allowSelfRegister !== false,
    requireApproval: reg.requireApproval !== false,
  };
}

/**
 * Registration window + status checks for new self / organizer creates.
 * @param {object | null | undefined} competition
 * @param {number} [nowMs]
 */
export function isRegistrationWindowOpen(competition, nowMs = Date.now()) {
  const status = normalizeCompetitionStatus(competition?.status);
  if (
    status === COMPETITION_STATUSES.ARCHIVED ||
    status === COMPETITION_STATUSES.COMPLETED ||
    status === COMPETITION_STATUSES.DELETED ||
    status === COMPETITION_STATUSES.DRAFT ||
    status === COMPETITION_STATUSES.REGISTRATION_CLOSED
  ) {
    return false;
  }
  const openMs = competition?.dates?.registrationOpenMs;
  const closeMs = competition?.dates?.registrationCloseMs;
  if (openMs != null && Number.isFinite(Number(openMs)) && nowMs < Number(openMs)) {
    return false;
  }
  if (closeMs != null && Number.isFinite(Number(closeMs)) && nowMs > Number(closeMs)) {
    return false;
  }
  return (
    status === COMPETITION_STATUSES.REGISTRATION_OPEN ||
    status === COMPETITION_STATUSES.PUBLISHED ||
    status === COMPETITION_STATUSES.DRAW_PUBLISHED
  );
}

/**
 * Public registration fact. A past close timestamp is "Closed", not "Closes …".
 * @param {object | null | undefined} competition
 * @param {number} [nowMs]
 * @returns {string | null}
 */
export function registrationFactLabel(competition, nowMs = Date.now()) {
  if (!competition) return null;
  const status = normalizeCompetitionStatus(competition.status);
  if (status === COMPETITION_STATUSES.DRAFT) return null;
  const openMs = competition?.dates?.registrationOpenMs;
  const closeMs = competition?.dates?.registrationCloseMs;
  const openAt = openMs != null && Number.isFinite(Number(openMs)) ? Number(openMs) : null;
  const closeAt = closeMs != null && Number.isFinite(Number(closeMs)) ? Number(closeMs) : null;
  if (!isRegistrationWindowOpen(competition, nowMs)) {
    if (
      openAt != null &&
      nowMs < openAt &&
      status !== COMPETITION_STATUSES.REGISTRATION_CLOSED
    ) {
      return `Opens ${new Date(openAt).toLocaleString()}`;
    }
    return "Closed";
  }
  if (closeAt != null && closeAt > nowMs) {
    return `Closes ${new Date(closeAt).toLocaleString()}`;
  }
  return null;
}

/**
 * Whether self-registration is offered (settings + window) — no auth required.
 * Use for Register CTAs on public / guest surfaces; create still needs uid.
 * @param {object | null | undefined} competition
 */
export function isSelfRegistrationOffered(competition) {
  if (!competition) return false;
  const { allowSelfRegister } = getRegistrationSettings(competition);
  if (!allowSelfRegister) return false;
  return isRegistrationWindowOpen(competition);
}

/**
 * Whether an authenticated player may self-register into this competition.
 * @param {object | null | undefined} competition
 * @param {string | null | undefined} uid
 */
export function canSelfRegisterEntry(competition, uid) {
  if (!uid || !competition) return false;
  return isSelfRegistrationOffered(competition);
}

/**
 * @param {object | null | undefined} competition
 */
function isTournamentArenaCompetition(competition) {
  if (!competition) return false;
  if (competition.mode === "tournament") return true;
  const modules = competition.enabledModules;
  return Array.isArray(modules) && modules.includes("tournament");
}

/**
 * @param {object | null | undefined} competition
 */
export function isCompetitionBrowseable(competition) {
  if (!competition) return false;
  // Casual room-owned draws stay in their session — not Discover/Browse.
  // Tournament Arenas always link a Live Session (roomId) for play; those must stay discoverable.
  if (competition.roomId && !isTournamentArenaCompetition(competition)) {
    return false;
  }
  const status = normalizeCompetitionStatus(competition.status);
  if (
    status === COMPETITION_STATUSES.DRAFT ||
    status === COMPETITION_STATUSES.ARCHIVED ||
    status === COMPETITION_STATUSES.DELETED
  ) {
    return false;
  }
  const visibility = competition.settings?.visibility || COMPETITION_VISIBILITY.PUBLIC;
  return visibility === COMPETITION_VISIBILITY.PUBLIC;
}

/**
 * @param {object} entry
 * @param {string} uid
 * @param {string | null} [playerId]
 */
export function entryOwnedByPlayer(entry, uid, playerId = null) {
  if (!entry || !uid) return false;
  if (entry.createdBy === uid) return true;
  const members = entry.members || [];
  if (members.some((m) => m.linkedUserId === uid)) return true;
  if (playerId && (entry.memberPlayerIds || []).includes(playerId)) return true;
  if (playerId && members.some((m) => m.playerId === playerId)) return true;
  return false;
}

/**
 * Player-facing registration state for an entry (or none).
 * @param {object | null} entry
 */
export function playerRegistrationState(entry) {
  if (!entry) return "not_registered";
  if (entry.status === ENTRY_STATUSES.PENDING) return "pending";
  if (entry.status === ENTRY_STATUSES.CONFIRMED) return "confirmed";
  if (entry.status === ENTRY_STATUSES.WITHDRAWN) return "withdrawn";
  if (entry.status === ENTRY_STATUSES.CANCELLED) return "rejected";
  return entry.status || "not_registered";
}

/**
 * Browse card / detail projection filters.
 * @param {object[]} competitions
 * @param {{ query?: string, status?: string, state?: string, club?: string, category?: string, sportId?: string }} filters
 */
export function filterBrowseCompetitions(competitions, filters = {}) {
  const q = (filters.query || "").trim().toLowerCase();
  const status = (filters.status || "").trim().toLowerCase();
  const state = (filters.state || "").trim().toLowerCase();
  const club = (filters.club || "").trim().toLowerCase();
  const category = (filters.category || "").trim().toLowerCase();
  const sportId = (filters.sportId || "").trim();
  const now = Date.now();

  return (competitions || []).filter((c) => {
    if (!isCompetitionBrowseable(c)) return false;
    if (sportId && !arenaMatchesSport(c, sportId)) return false;
    if (status === "upcoming") {
      const start = c.dates?.startMs;
      if (start != null && Number(start) < now) return false;
      const st = normalizeCompetitionStatus(c.status);
      if (
        st === COMPETITION_STATUSES.COMPLETED ||
        st === COMPETITION_STATUSES.IN_PROGRESS
      ) {
        return false;
      }
    } else if (status === "open") {
      if (!isRegistrationWindowOpen(c) || !getRegistrationSettings(c).allowSelfRegister) {
        return false;
      }
    } else if (status && normalizeCompetitionStatus(c.status) !== status) {
      return false;
    }
    if (state) {
      const blob = `${c.venue?.state || ""} ${c.venue?.city || ""}`.toLowerCase();
      if (!blob.includes(state)) return false;
    }
    if (club) {
      const name = `${c.name || ""} ${c.organizationId || ""}`.toLowerCase();
      if (!name.includes(club)) return false;
    }
    if (category) {
      const events = c.managedEvents || [];
      const hit = events.some((ev) =>
        `${ev.name || ""} ${ev.category || ""} ${ev.eventType || ""}`
          .toLowerCase()
          .includes(category)
      );
      if (!hit) return false;
    }
    if (q) {
      const hay = `${c.name || ""} ${c.venue?.city || ""} ${c.venue?.name || ""} ${c.description || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/**
 * @param {object} competition
 */
export function projectBrowseCompetitionCard(competition) {
  const entries = competition.managedEntries || [];
  const active = entries.filter(
    (e) => e.status === ENTRY_STATUSES.PENDING || e.status === ENTRY_STATUSES.CONFIRMED
  );
  const storedCount = Number(competition.entryCount);
  const entryCount =
    active.length > 0
      ? active.length
      : Number.isFinite(storedCount) && storedCount >= 0
        ? storedCount
        : 0;
  const reg = getRegistrationSettings(competition);
  const open = isRegistrationWindowOpen(competition) && reg.allowSelfRegister;
  return {
    competitionId: competition.competitionId || competition.id,
    name: competition.name || "Tournament",
    status: competition.status,
    venue: competition.venue || null,
    dates: competition.dates || null,
    events: (competition.managedEvents || []).map((e) => ({
      eventId: e.eventId,
      name: e.name || "Event",
      category: e.category || e.eventType || null,
    })),
    entryCount,
    registrationOpen: open,
    registrationCloseMs: competition.dates?.registrationCloseMs ?? null,
    allowSelfRegister: reg.allowSelfRegister,
    requireApproval: reg.requireApproval,
  };
}
